import Head from 'next/head'
import PageTitle from '../../components/page-title'
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import Location from '../../components/location'
import { LinkButtonGroup } from '../../components/button'
import {
  getEnabledConciergeLocations,
  getEnabledConciergeLocationsKey,
} from '../../api-client/listing'
import useLocation from '../../hooks/use-location'
import getClosestLocation from '../../utils/get-closest-location'

export default function ContactUs() {
  const { query } = useRouter()

  const [userLocation] = useLocation()

  const [conciergeName, setConciergeName] = useState('')

  const { data: locations = [], isFetching: locationsFetching } = useQuery({
    queryKey: getEnabledConciergeLocationsKey,
    queryFn: getEnabledConciergeLocations,
    keepPreviousData: true,
  })

  const location = useMemo(() => {
    if (!!locationsFetching) {
      console.log('locations fetching')
      return null
    }

    if (!locations) {
      console.log('locations is null')
      return null
    }
    if (locations.length == 0) {
      console.log('locations is empty')
      return null
    }

    setConciergeName(query.conciergeName)

    let currentLocation = locations.find((loc) => loc.value == conciergeName)
    if (!!currentLocation) {
      console.log('found location by name', conciergeName)
    }
    if (!currentLocation) {
      let closest = getClosestLocation(locations, userLocation, 'springdale')
      currentLocation = closest
    }
    if (!currentLocation) {
      console.log('defaulting to first location in list')
      currentLocation = locations[0] // should be springdale
    }

    return currentLocation
  }, [conciergeName, locations, query, userLocation, locationsFetching])

  return (
    <>
      <Head>
        <title>
          {location?.pageTitle ?? 'The Best Website To Sell or Buy A Car | Contact privauto'}
        </title>
        <meta
          name='description'
          content={
            location?.pageDescription ??
            'Contact privauto at DriversExchange.com to learn how you can buy or sell a car safely and save money using our online website with no middle man.'
          }
        />
      </Head>

      {!!location && (
        <section>
          <section className='title'>
            <PageTitle>Contact Us - {location.stateName}</PageTitle>
          </section>

          <section className='contactInfo'>
            <div className='container'>
              <div className='wrapper flex'>
                <div className='info'>
                  <address>
                    <h3>Concierge Address</h3>
                    <p>
                      {location.address1}
                      <br />
                      {location.address2}
                      <br />
                      {location.address3}
                    </p>
                  </address>
                  <address>
                    <h3>Title and Funding</h3>
                    <p>
                      For title and funding assistance please call or email <br />
                      Toll-free: <a href='tel:1-844-300-2222'>1-844-300-2222</a> <br />
                      Email:{' '}
                      <a className='mail' href='mailto:info@drivrzxchange.com'>
                        info@drivrzxchange.com
                      </a>
                      <br />
                    </p>
                  </address>
                  <address>
                    <h3>Technical Support</h3>
                    <p>
                      For account verification and technical assistance please email <br />
                      Email:{' '}
                      <a className='mail' href='mailto:support@drivrzxchange.com'>
                        support@drivrzxchange.com
                      </a>
                    </p>
                  </address>
                </div>
                <div className='location'>
                  <div>
                    <LinkButtonGroup
                      name='conciergeLocationName'
                      label='Find a concierge location'
                      value={location.value}
                      options={locations}
                      buttonSize='auto'
                    />
                    <br />
                  </div>
                  <div className='mapContainer'>
                    <Location
                      lat={location.lat}
                      lng={location.lng}
                      title={location.address3}
                      allLocations={locations}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </section>
      )}

      <style jsx>{`
        .title {
          padding: 5.1rem 0 4.7rem;

          :global(h1) {
            text-align: center;
          }
        }

        .label {
          margin: 3rem 0 1.1rem;
          color: var(--primary-text-color);
          font-size: 1.6rem;
          font-weight: 700;
        }

        .contactInfo {
          padding: 4.7rem 0 5.8rem;
          background: var(--primary-bg-color);
        }

        @media (max-width: 1024px) {
          .title {
            padding: 2.5rem 1.2rem 2.7rem;
          }

          .contactInfo {
            padding: 1.7rem 0 0;
          }
        }

        .container {
          max-width: 193rem;
          width: 100%;
          margin: 0 auto;
          padding: 0 4rem;
        }

        .wrapper {
          max-width: 166.6rem;
          width: 100%;
          margin: auto;
        }

        h3 {
          line-height: 3.6rem;
          font-size: 3.2rem;
        }

        .info {
          width: 20%;
          min-width: 36.3rem;
          padding: 0 2rem 0 0;

          address {
            margin: 0 0 2.6rem;
            font-style: normal;

            h3 {
              margin: 0 0 0.8rem;
            }
          }

          p {
            line-height: 2.3rem;
            font-size: 1.8rem;
          }

          .mail,
          button {
            color: var(--text-link-color);
          }
        }

        .location {
          width: 80%;

          h3 {
            margin: 0 0 3.7rem;
            text-align: center;
          }
        }

        .mapContainer {
          border-radius: 1rem;
          overflow: hidden;
        }

        @media (max-width: 1024px) {
          .container {
            padding: 0;
          }

          .wrapper {
            flex-direction: column;
            align-items: center;
          }

          .info {
            padding: 0;

            address {
              margin: 0 0 1.9rem;

              h3 {
                margin: 0 0 0.6rem;
              }

              p {
                line-height: 1.8rem;
                text-align: center;
                font-size: 1.4rem;
              }
            }
          }

          .location {
            width: 100%;

            h3 {
              margin: 0 0 2.2rem;
            }
          }

          h3 {
            line-height: 2.6rem;
            text-align: center;
            font-size: 2rem;
          }

          .mapContainer {
            border-radius: 0;
          }
        }
      `}</style>
    </>
  )
}

ContactUs.getInitialProps = () => ({
  namespacesRequired: ['common'],
})
